import React, { useState, useEffect, useRef } from 'react';
import { assessmentsApi } from '../../services/api';
import toast from 'react-hot-toast';
import { HiX, HiPlus, HiTrash, HiOutlineUpload } from 'react-icons/hi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { usePermissions } from '../../hooks/usePermissions';
import { formatValidationErrors } from '../../utils/errorHandler';
import { todayYmdIST, formatYmdIST } from '../../utils/timezoneDisplay';
import { getCached } from '../../services/lookupCache';
import AssessmentDocumentsBlock from '../Assessments/AssessmentDocumentsBlock';

function normalizeDateYmd(value) {
  if (!value) return '';
  if (typeof value === 'string') return value.split('T')[0].slice(0, 10);
  return formatYmdIST(new Date(value));
}

function hasRichTextContent(html) {
  if (!html) return false;
  const stripped = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  return stripped.length > 0;
}

const AssessmentForm = ({ assessment, leadData, reassessmentContext, onClose, onSuccess }) => {
  const { canCreate, canUpdate } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [diagnosisCodes, setDiagnosisCodes] = useState([]);
  const [formData, setFormData] = useState({
    assessment_date: todayYmdIST(),
    reassessment_date: todayYmdIST(),
    is_reassessment: false,
    parent_assessment_id: null,
    give_home_plan: false,
    home_plan_text: '',
    chief_complaints: '',
    medical_history: '',
    observations: '',
    recommendations: '',
    therapy_suggestion: '',
    fee: '1200',
    fee_waived: false,
    waiver_reason: '',
    notes: '',
    diagnoses: []
  });
  const [selectedDiagnoses, setSelectedDiagnoses] = useState([]); // For checkbox selection
  const [customDiagnosis, setCustomDiagnosis] = useState('');
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const fileInputRef = useRef(null);
  const homePlanToolbarModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['clean']
    ]
  };
  const isReassessmentMode = !assessment && !!reassessmentContext;
  const isReassessmentEditMode =
    assessment?.is_reassessment === true
    || assessment?.is_reassessment === 1
    || assessment?.is_reassessment === '1';
  const isMinimalReassessmentMode = isReassessmentMode || isReassessmentEditMode;

  useEffect(() => {
    fetchDiagnosisCodes();
    if (assessment) {
      const assessmentDate = normalizeDateYmd(assessment.assessment_date) || todayYmdIST();
      const reassessmentDate =
        normalizeDateYmd(assessment.reassessment_date) || assessmentDate;
      const isReassessmentRecord =
        assessment.is_reassessment === true
        || assessment.is_reassessment === 1
        || assessment.is_reassessment === '1';

      setFormData(prev => ({
        ...prev,
        assessment_date: assessmentDate,
        reassessment_date: reassessmentDate,
        is_reassessment: isReassessmentRecord,
        parent_assessment_id: assessment.parent_assessment_id || null,
        give_home_plan:
          assessment.give_home_plan === true
          || assessment.give_home_plan === 1
          || assessment.give_home_plan === '1'
          || hasRichTextContent(assessment.home_plan_text),
        home_plan_text: assessment.home_plan_text || '',
        chief_complaints: assessment.chief_complaints || '',
        medical_history: assessment.medical_history || '',
        observations: assessment.observations || '',
        recommendations: assessment.recommendations || '',
        therapy_suggestion: assessment.therapy_suggestion || '',
        fee: assessment.fee || '1200',
        fee_waived: assessment.fee_waived || false,
        waiver_reason: assessment.waiver_reason || '',
        notes: assessment.notes || '',
        diagnoses: assessment.diagnoses || []
      }));
      
      const firstDocUrl =
        assessment.document_url
        || (Array.isArray(assessment.documents) && assessment.documents.find((doc) => doc?.url)?.url)
        || null;
      setDocumentUrl(firstDocUrl);
      setPendingDocuments([]);
    } else if (reassessmentContext) {
      setFormData(prev => ({
        ...prev,
        assessment_date: todayYmdIST(),
        reassessment_date: todayYmdIST(),
        is_reassessment: true,
        parent_assessment_id: reassessmentContext.parent_assessment_id || null,
        give_home_plan: false,
        home_plan_text: '',
      }));
      setPendingDocuments([]);
      setDocumentUrl(null);
    }
  }, [assessment, leadData, reassessmentContext]);

  const fetchDiagnosisCodes = async () => {
    try {
      const response = await getCached(
        'diagnosis:codes',
        () => assessmentsApi.getDiagnosisCodes()
      );
      setDiagnosisCodes(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch diagnosis codes');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDiagnosisCheckboxChange = (diagnosisId, checked) => {
    if (checked) {
      setSelectedDiagnoses(prev => [...prev, diagnosisId]);
    } else {
      setSelectedDiagnoses(prev => prev.filter(id => id !== diagnosisId));
    }
  };

  const handleAddSelectedDiagnoses = () => {
    if (selectedDiagnoses.length === 0 && !customDiagnosis.trim()) {
      toast.error('Please select at least one diagnosis or enter a custom diagnosis');
      return;
    }

    const newDiagnoses = [];
    const existingDiagnosisIds = formData.diagnoses.map(d => d.diagnosis_id?.toString()).filter(Boolean);
    const existingCustomDiagnoses = formData.diagnoses.map(d => d.custom_diagnosis?.toLowerCase().trim()).filter(Boolean);

    // Add selected diagnosis codes (prevent duplicates)
    selectedDiagnoses.forEach(diagnosisId => {
      // Check if already added
      if (existingDiagnosisIds.includes(diagnosisId)) {
        return; // Skip if already added
      }
      
      const selectedCode = diagnosisCodes.find(d => d.id === parseInt(diagnosisId));
      if (selectedCode) {
        newDiagnoses.push({
          diagnosis_id: diagnosisId,
          custom_diagnosis: null,
          diagnosis_name: selectedCode.name,
          code: selectedCode.code,
          notes: null
        });
      }
    });

    // Add custom diagnosis if provided (prevent duplicates)
    if (customDiagnosis.trim()) {
      const customLower = customDiagnosis.trim().toLowerCase();
      if (!existingCustomDiagnoses.includes(customLower)) {
        newDiagnoses.push({
          diagnosis_id: null,
          custom_diagnosis: customDiagnosis.trim(),
          diagnosis_name: customDiagnosis.trim(),
          code: null,
          notes: null
        });
      } else {
        toast.error('This custom diagnosis has already been added');
      }
    }

    if (newDiagnoses.length === 0) {
      toast.error('All selected diagnoses have already been added');
      return;
    }

    setFormData(prev => ({
      ...prev,
      diagnoses: [...prev.diagnoses, ...newDiagnoses]
    }));

    // Clear selections
    setSelectedDiagnoses([]);
    setCustomDiagnosis('');
  };

  // Get available diagnosis codes (exclude already added ones)
  const getAvailableDiagnosisCodes = () => {
    const addedDiagnosisIds = formData.diagnoses
      .map(d => d.diagnosis_id?.toString())
      .filter(Boolean);
    
    return diagnosisCodes.filter(code => !addedDiagnosisIds.includes(code.id.toString()));
  };

  const handleRemoveDiagnosis = (index) => {
    setFormData(prev => ({
      ...prev,
      diagnoses: prev.diagnoses.filter((_, i) => i !== index)
    }));
  };

  const ALLOWED_DOCUMENT_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp'
  ];
  const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;

  const validateDocumentFile = (file) => {
    if (!ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
      toast.error(`${file.name}: only PDF, DOC, DOCX, and image files are allowed`);
      return false;
    }
    if (file.size > MAX_DOCUMENT_BYTES) {
      toast.error(`${file.name}: file size must be less than 10MB`);
      return false;
    }
    return true;
  };

  const uploadDocumentFile = async (assessmentId, file) => {
    const payload = new FormData();
    payload.append('document', file);
    const response = await assessmentsApi.uploadDocument(assessmentId, payload);
    if (response.data?.data?.document_url) {
      setDocumentUrl(response.data.data.document_url);
    }
  };

  const handleDocumentFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;

    const validFiles = files.filter(validateDocumentFile);
    if (validFiles.length === 0) return;

    if (assessment?.id) {
      setUploadingDocument(true);
      try {
        for (const file of validFiles) {
          await uploadDocumentFile(assessment.id, file);
        }
        toast.success(validFiles.length > 1 ? 'Documents uploaded successfully' : 'Document uploaded successfully');
        if (onSuccess) onSuccess();
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to upload document';
        toast.error(errorMessage);
      } finally {
        setUploadingDocument(false);
      }
      return;
    }

    setPendingDocuments((prev) => [...prev, ...validFiles].slice(0, 10));
  };

  const handleReplaceDocument = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removePendingDocument = (index) => {
    setPendingDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const hasOptionalDetails = () => {
    return [
      formData.chief_complaints,
      formData.medical_history,
      formData.observations,
      formData.recommendations,
      formData.therapy_suggestion,
      formData.notes,
    ].some((value) => String(value || '').trim().length > 0)
      || (formData.diagnoses || []).length > 0
      || hasRichTextContent(formData.home_plan_text)
      || pendingDocuments.length > 0
      || !!documentUrl
      || !!(assessment && assessment.document_file_path);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check permissions
    if (assessment && !canUpdate('assessments')) {
      toast.error('You do not have permission to update assessments');
      return;
    }
    if (!assessment && !canCreate('assessments')) {
      toast.error('You do not have permission to create assessments');
      return;
    }
    
    // Validate assessment_date
    if (!formData.assessment_date) {
      toast.error('Assessment date is required');
      return;
    }
    if (formData.is_reassessment && !formData.reassessment_date) {
      toast.error('Re-assessment date is required');
      return;
    }
    if (isMinimalReassessmentMode && !hasOptionalDetails()) {
      toast.error('Upload the physical form (PDF/photos) or fill at least one detail');
      return;
    }
    
    setLoading(true);

    try {
      const isMinimalReassessment = isMinimalReassessmentMode || !!formData.is_reassessment;
      const resolvedParentAssessmentId =
        formData.parent_assessment_id ||
        reassessmentContext?.parent_assessment_id ||
        null;
      const resolvedPatientIdRaw = reassessmentContext?.patient_id;
      const resolvedPatientId =
        resolvedPatientIdRaw !== undefined &&
        resolvedPatientIdRaw !== null &&
        resolvedPatientIdRaw !== '' &&
        Number.isFinite(Number(resolvedPatientIdRaw)) &&
        Number(resolvedPatientIdRaw) > 0
          ? Number(resolvedPatientIdRaw)
          : null;
      const homePlanHasContent = hasRichTextContent(formData.home_plan_text);
      const giveHomePlan = !!formData.give_home_plan || homePlanHasContent;
      // Prepare data for submission - only send required fields for diagnoses
      const submitData = {
        // Fresh assessment: omit patient_id until convert-to-patient (null fails express-validator isInt)
        ...(resolvedPatientId ? { patient_id: resolvedPatientId } : {}),
        ...(leadData?.lead_id ? { lead_id: Number(leadData.lead_id) } : {}),
        assessment_date: isMinimalReassessment ? formData.reassessment_date : formData.assessment_date,
        is_reassessment: isMinimalReassessment ? true : !!formData.is_reassessment,
        parent_assessment_id: resolvedParentAssessmentId,
        reassessment_date: isMinimalReassessment ? formData.reassessment_date : null,
        give_home_plan: isMinimalReassessment ? giveHomePlan : !!formData.give_home_plan,
        home_plan_text: isMinimalReassessment
          ? (giveHomePlan ? (formData.home_plan_text || null) : null)
          : (formData.give_home_plan ? (formData.home_plan_text || null) : null),
        chief_complaints: formData.chief_complaints || null,
        medical_history: formData.medical_history || null,
        observations: formData.observations || null,
        recommendations: formData.recommendations || null,
        therapy_suggestion: formData.therapy_suggestion || null,
        fee: isMinimalReassessment ? 0 : (formData.fee ? parseFloat(formData.fee) : 0),
        fee_waived: isMinimalReassessment ? false : (formData.fee_waived || false),
        waiver_reason: isMinimalReassessment ? null : (formData.waiver_reason || null),
        notes: formData.notes || null,
        diagnoses: formData.diagnoses.map(diag => {
          // Normalize diagnosis_id: convert empty string to null, ensure it's a number if provided
          let diagnosisId = diag.diagnosis_id;
          if (diagnosisId === '' || diagnosisId === undefined || diagnosisId === null) {
            diagnosisId = null;
          } else {
            diagnosisId = parseInt(diagnosisId) || null;
          }

          return {
            diagnosis_id: diagnosisId,
            custom_diagnosis: diag.custom_diagnosis && diag.custom_diagnosis.trim() !== '' ? diag.custom_diagnosis.trim() : null,
            notes: diag.notes && diag.notes.trim() !== '' ? diag.notes.trim() : null
          };
        }).filter(diag => diag.diagnosis_id !== null || diag.custom_diagnosis !== null) // Remove invalid diagnoses
      };

      if (assessment) {
        await assessmentsApi.update(assessment.id, submitData);
        if (pendingDocuments.length > 0) {
          for (const file of pendingDocuments) {
            await uploadDocumentFile(assessment.id, file);
          }
          setPendingDocuments([]);
        }
        toast.success(isMinimalReassessment ? 'Re-assessment updated successfully' : 'Assessment updated successfully');
      } else {
        const created = await assessmentsApi.create(submitData);
        const createdId = created.data?.data?.id;
        if (createdId && pendingDocuments.length > 0) {
          try {
            for (const file of pendingDocuments) {
              await uploadDocumentFile(createdId, file);
            }
            setPendingDocuments([]);
          } catch (uploadError) {
            toast.error('Re-assessment saved, but a document failed to upload. You can add it from View.');
            onSuccess();
            onClose();
            return;
          }
        }
        toast.success(isMinimalReassessment ? 'Re-assessment created successfully' : 'Assessment created successfully');
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(formatValidationErrors(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">
                {assessment
                  ? (isReassessmentEditMode ? 'Edit Re-Assessment' : 'Edit Assessment')
                  : isReassessmentMode
                  ? 'Create Re-Assessment'
                  : leadData
                  ? 'Create Assessment from Lead'
                  : 'Create New Assessment'}
              </h3>
              {!assessment && !isMinimalReassessmentMode && (
                <p className="text-sm text-gray-500 mt-1">
                  Create assessment first. Patient will be created after converting assessment to patient.
                </p>
              )}
              {isMinimalReassessmentMode && (
                <p className="text-sm text-gray-500 mt-1">
                  Upload the physical form (PDF or photos), fill details here, or both. Clinical fields are optional.
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <HiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-4">
            {!isMinimalReassessmentMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assessment Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="assessment_date"
                value={formData.assessment_date}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            )}
            {isMinimalReassessmentMode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Re-Assessment Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="reassessment_date"
                  value={formData.reassessment_date}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-1">
              {isMinimalReassessmentMode ? 'Physical form (PDF / photos)' : 'Document'}
            </h4>
            <p className="text-xs text-gray-500 mb-3">
              {isMinimalReassessmentMode
                ? 'If the doctor filled a paper form, attach the PDF or photos here. You can skip typed details below.'
                : 'Supported formats: PDF, DOC, DOCX, JPG, PNG, GIF, WEBP (Max 10MB each)'}
            </p>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
              onChange={handleDocumentFileChange}
              className="hidden"
              disabled={uploadingDocument}
            />

            {(documentUrl || assessment?.document_file_path || (assessment?.documents || []).length > 0) ? (
              <div className="mb-3 space-y-3">
                <AssessmentDocumentsBlock
                  embedded
                  title=""
                  assessment={{
                    ...assessment,
                    document_url: documentUrl || assessment?.document_url,
                    document_file_path: assessment?.document_file_path,
                    documents: assessment?.documents,
                  }}
                />
                <button
                  type="button"
                  onClick={handleReplaceDocument}
                  disabled={uploadingDocument}
                  className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
                >
                  <HiOutlineUpload className="w-4 h-4" />
                  {uploadingDocument ? 'Uploading...' : 'Add more'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleReplaceDocument}
                disabled={uploadingDocument}
                className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <HiOutlineUpload className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">
                  {uploadingDocument ? 'Uploading...' : 'Upload PDF or photos'}
                </span>
              </button>
            )}

            {pendingDocuments.length > 0 && (
              <ul className="mt-3 space-y-2">
                {pendingDocuments.map((file, index) => (
                  <li
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  >
                    <span className="truncate text-gray-800">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removePendingDocument(index)}
                      className="ml-2 text-red-600 hover:text-red-700"
                      aria-label="Remove file"
                    >
                      <HiTrash className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {isMinimalReassessmentMode && (
              <p className="mt-2 text-xs text-gray-500">
                Files are saved when you create the re-assessment.
              </p>
            )}
          </div>

          {/* Home plan + rich suggestion: re-assessment only (not fresh / full assessment) */}
          {isMinimalReassessmentMode && (
          <div className="border-t border-gray-200 pt-4">
            <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                name="give_home_plan"
                checked={formData.give_home_plan}
                onChange={handleChange}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              Give Home Plan
            </label>
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Suggestion (Rich Text)
              </label>
              <ReactQuill
                key={`home-plan-${assessment?.id || reassessmentContext?.parent_assessment_id || 'new'}`}
                theme="snow"
                value={formData.home_plan_text || ''}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, home_plan_text: value }))
                }
                modules={homePlanToolbarModules}
                className="bg-white"
                style={{ height: 260, marginBottom: 44 }}
              />
            </div>
          </div>
          )}

          {isMinimalReassessmentMode && (
            <p className="text-sm font-medium text-gray-700 border-t border-gray-200 pt-4">
              Optional details <span className="font-normal text-gray-500">(skip if the physical form is uploaded)</span>
            </p>
          )}

          <>
          {/* Chief Complaints */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Chief Complaints
            </label>
            <textarea
              name="chief_complaints"
              value={formData.chief_complaints}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Describe the main complaints..."
            />
          </div>

          {/* Medical History */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Medical History
            </label>
            <textarea
              name="medical_history"
              value={formData.medical_history}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Relevant medical history..."
            />
          </div>

          {/* Observations */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observations
            </label>
            <textarea
              name="observations"
              value={formData.observations}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Clinical observations during assessment..."
            />
          </div>

          {/* Recommendations */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recommendations
            </label>
            <textarea
              name="recommendations"
              value={formData.recommendations}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Treatment recommendations..."
            />
          </div>

          {/* Therapy Suggestion */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Therapy Suggestion
              </label>
              <input
                type="text"
                name="therapy_suggestion"
                value={formData.therapy_suggestion}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., Occupational Therapy, Speech Therapy"
              />
            </div>

          </div>

          {/* Fee Information */}
          {!isMinimalReassessmentMode && (
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Fee Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assessment Fee (₹)
                </label>
                <input
                  type="number"
                  name="fee"
                  value={formData.fee}
                  onChange={handleChange}
                  min="0"
                  max="9999"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0"
                />
              </div>

              <div className="flex items-end">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="fee_waived"
                    checked={formData.fee_waived}
                    onChange={handleChange}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label className="ml-2 text-sm text-gray-700">Fee Waived</label>
                </div>
              </div>
            </div>

            {formData.fee_waived && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Waiver Reason
                </label>
                <input
                  type="text"
                  name="waiver_reason"
                  value={formData.waiver_reason}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Reason for fee waiver"
                />
              </div>
            )}
          </div>
          )}

          {/* Diagnoses */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Diagnoses</h4>
            
            {/* Diagnosis Selection with Dropdown Checkboxes */}
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Diagnoses (Dropdown)</label>
                <div className="relative">
                  <div className="border border-gray-300 rounded-lg bg-white">
                    <div className="max-h-60 overflow-y-auto p-2">
                      {getAvailableDiagnosisCodes().length === 0 ? (
                        <p className="text-sm text-gray-500 p-2">All diagnoses have been added</p>
                      ) : (
                        <div className="space-y-1">
                          {getAvailableDiagnosisCodes().map((code) => (
                            <label key={code.id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedDiagnoses.includes(code.id.toString())}
                                onChange={(e) => handleDiagnosisCheckboxChange(code.id.toString(), e.target.checked)}
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-700">
                                <span className="font-medium">{code.code}</span> - {code.name}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Or Enter Custom Diagnosis</label>
                <input
                  type="text"
                  value={customDiagnosis}
                  onChange={(e) => setCustomDiagnosis(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter custom diagnosis"
                />
              </div>
              
              <button
                type="button"
                onClick={handleAddSelectedDiagnoses}
                disabled={selectedDiagnoses.length === 0 && !customDiagnosis.trim()}
                className="w-full px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <HiPlus className="w-4 h-4" />
                Add Selected Diagnoses
              </button>
            </div>

            {/* List of Diagnoses */}
            {formData.diagnoses.length > 0 && (
              <div className="space-y-2">
                {formData.diagnoses.map((diag, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="font-medium text-sm">
                        {diag.code ? `${diag.code} - ` : ''}{diag.diagnosis_name || diag.custom_diagnosis}
                      </span>
                      {diag.notes && (
                        <p className="text-xs text-gray-500 mt-1">{diag.notes}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveDiagnosis(index)}
                      className="text-red-600 hover:text-red-700 p-1"
                    >
                      <HiTrash className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Additional Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Any additional notes..."
            />
          </div>

          </>
          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploadingDocument}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? 'Saving...'
                : assessment
                ? (isReassessmentEditMode ? 'Update Re-Assessment' : 'Update Assessment')
                : isReassessmentMode
                ? 'Create Re-Assessment'
                : 'Create Assessment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssessmentForm;

